import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { enabled: true, host: "", port: 465, username: "", password: "", from_email: "", reply_to: "" };
const FIELDS = [["host", "Server SMTP", "authsmtp.securemail.pro"], ["port", "Porta", "465"], ["username", "Utente", "no-replay@nouvellecrm.it"], ["password", "Password", ""],
  ["from_email", "Indirizzo mittente", "no-replay@nouvellecrm.it"], ["reply_to", "Rispondi a (facoltativo)", "info@azienda.it"]];

export function ResendStatus() {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [to, setTo] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/email-resend").then((r) => setSt(r.data)).catch(() => {}); }, []);
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/email-resend/test", { to }); toast.success(r.data.message); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="resend-status">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`inline-flex h-2.5 w-2.5 rounded-full ${st.configured ? "bg-emerald-500" : "bg-amber-400"}`} />
        <span className="font-medium text-slate-800">Resend (canale principale)</span>
        <span className="text-slate-500" data-testid="resend-configured">{st.configured ? "configurato" : "chiave RESEND_API_KEY non impostata: le email usano SMTP"}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">Mittente <span className="font-mono">{st.from_email}</span> · assistenza/ticket <span className="font-mono">{st.support_email}</span>. Ordine di invio: Resend → SMTP → servizio piattaforma.</p>
      {st.configured && <div className="mt-3 flex items-end gap-2"><Input data-testid="resend-test-to" className="w-64 text-sm" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario prova" />
        <Button size="sm" variant="outline" data-testid="resend-test" disabled={busy} onClick={test}><Send className="mr-1.5 h-3.5 w-3.5" />{busy ? "Invio..." : "Prova Resend"}</Button></div>}
    </div>
  );
}

export function SmtpSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(EMPTY);
  const [hasPwd, setHasPwd] = useState(false);
  const [to, setTo] = useState(user?.email || "");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/tenants-smtp").then((r) => { setS({ ...EMPTY, ...r.data, password: "" }); setHasPwd(!!r.data.has_password); }).catch(() => {}); }, []);

  const save = async () => {
    setBusy("save");
    try { const r = await api.put("/tenants-smtp", { ...s, port: Number(s.port) }); setS({ ...EMPTY, ...r.data, password: "" }); setHasPwd(!!r.data.has_password); toast.success("Mittente email salvato"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const r = await api.post("/tenants-smtp/test", { to }); toast.success(r.data.message); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const reset = async () => {
    if (!window.confirm("Tornare al servizio email della piattaforma?")) return;
    try { await api.delete("/tenants-smtp"); setS(EMPTY); setHasPwd(false); toast.success("Configurazione rimossa"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Panel title={<span className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" />Mittente email per tutti i circuiti</span>} testId="smtp-settings">
      <div className="space-y-4 p-4">
        <ResendStatus />
        <p className="text-sm text-slate-500">Fallback SMTP: se Resend non è configurato o non risponde, report, avvisi e recupero password partiranno da questo indirizzo (es. <span className="font-mono">no-replay@nouvellecrm.it</span>); il nome del mittente resta quello del circuito. Se anche il server SMTP non risponde si usa il servizio della piattaforma.</p>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="smtp-enabled-switch" checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />Usa questo mittente</label>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map(([k, l, ph]) => (
            <div key={k} className="space-y-1"><Label className="text-xs text-slate-500">{l}</Label>
              <Input data-testid={`smtp-${k}-input`} type={k === "password" ? "password" : "text"} className={k === "port" ? "w-28 font-mono" : "font-mono text-sm"} value={s[k] ?? ""} placeholder={k === "password" && hasPwd ? "•••••••• (salvata, lascia vuoto per non cambiare)" : ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></div>))}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button size="sm" data-testid="smtp-save" disabled={!!busy} onClick={save}>{busy === "save" ? "Salvo..." : "Salva"}</Button>
          <div className="flex items-end gap-2"><Input data-testid="smtp-test-to" className="w-64 text-sm" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario prova" />
            <Button size="sm" variant="outline" data-testid="smtp-test" disabled={!!busy || !hasPwd} onClick={test}><Send className="mr-1.5 h-3.5 w-3.5" />{busy === "test" ? "Invio..." : "Invia prova"}</Button></div>
          {hasPwd && <Button size="sm" variant="ghost" className="text-rose-600" data-testid="smtp-reset" onClick={reset}>Rimuovi</Button>}
        </div>
      </div>
    </Panel>
  );
}
